/**
 * PDF Thumbnail Generation Utility
 *
 * Client-side PDF thumbnail generation using PDF.js
 * Renders the first page of a PDF to a canvas and converts to data URL
 */

import * as pdfjsLib from 'pdfjs-dist';
import { devLog } from '@/utils/logger';

// Set worker source - use local worker from node_modules (bundled by Vite)
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

export interface PDFThumbnailOptions {
  /**
   * Maximum width of thumbnail (default: 400px)
   */
  maxWidth?: number;
  /**
   * Maximum height of thumbnail (default: 400px)
   */
  maxHeight?: number;
  /**
   * Image quality for JPEG (0-1, default: 0.9)
   */
  quality?: number;
  /**
   * Output format (default: 'image/jpeg')
   */
  format?: 'image/jpeg' | 'image/png';
}

/**
 * Generate a thumbnail from the first page of a PDF file
 *
 * @param file - PDF file
 * @param options - Thumbnail generation options
 * @returns Data URL of the thumbnail image
 */
export async function generatePDFThumbnail(
  file: File,
  options: PDFThumbnailOptions = {}
): Promise<string> {
  const {
    maxWidth = 400,
    maxHeight = 400,
    quality = 0.9,
    format = 'image/jpeg',
  } = options;

  try {
    // Convert File to ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Load PDF document
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;

    // Get first page
    const page = await pdf.getPage(1);

    // Calculate scale to fit within max dimensions while maintaining aspect ratio
    const viewport = page.getViewport({ scale: 1 });
    const scale = Math.min(
      maxWidth / viewport.width,
      maxHeight / viewport.height,
      2 // Max scale of 2 for quality
    );

    const scaledViewport = page.getViewport({ scale });

    // Create canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Failed to get canvas context');
    }

    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;

    // Render PDF page to canvas
    const renderContext = {
      canvasContext: context,
      viewport: scaledViewport,
      canvas, // Required by RenderParameters interface
    };

    await page.render(renderContext).promise;

    // Convert canvas to data URL
    const dataUrl = canvas.toDataURL(format, quality);

    // Cleanup
    pdf.destroy();

    return dataUrl;
  } catch (error) {
    devLog.error('[PDFThumbnail] Failed to generate thumbnail:', error);
    throw new Error(
      `Failed to generate PDF thumbnail: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Check if a file is a PDF
 */
export function isPDF(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}
